"use client";

import React, { useMemo } from "react";
import { ArrowRight, Layers, TableProperties } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import DataTable, { type DataTableColumn } from "@/components/common/DataTable";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface MatrixItem {
  mesh: string;
  wire: string;
  aperture: string;
  openArea: string;
  type: Record<Lang, string>;
}
const meshMatrixPreview: MatrixItem[] = [{
  mesh: "20 x 20",
  wire: "0.40 mm",
  aperture: "0.87 mm",
  openArea: "46.9%",
  type: {
    en: "Square Mesh",
    zh: "方孔网",
    es: "Malla cuadrada",
    de: "Quadratmasche",
    fr: "Maille carrée",
    pt: "Malha quadrada"
  }
}, {
  mesh: "100 x 100",
  wire: "0.10 mm",
  aperture: "0.154 mm",
  openArea: "36.8%",
  type: {
    en: "Fine Filter",
    zh: "精密滤网",
    es: "Filtro fino",
    de: "Feinfilter",
    fr: "Filtre fin",
    pt: "Filtro fino"
  }
}, {
  mesh: "200 x 1400",
  wire: "0.07 / 0.04 mm",
  aperture: "10 µm",
  openArea: "Nominal",
  type: {
    en: "Dutch Weave",
    zh: "荷兰编织",
    es: "Tejido Dutch",
    de: "Tressengewebe",
    fr: "Tissage hollandais",
    pt: "Trama holandesa"
  }
}, {
  mesh: "5-Layer Spec",
  wire: "Multilayer",
  aperture: "5 µm - 100 µm",
  openArea: "Custom",
  type: {
    en: "Sintered Mesh",
    zh: "烧结网板",
    es: "Malla sinterizada",
    de: "Sintergewebe",
    fr: "Toile frittée",
    pt: "Malha sinterizada"
  }
}];
const copyData = {
  eyebrow: {
    en: "Standard Specifications",
    zh: "常用典型规格",
    es: "Especificaciones estándar",
    de: "Standardspezifikationen",
    fr: "Spécifications standard",
    pt: "Especificações padrão"
  },
  heading: {
    en: "Selected standard mesh specifications",
    zh: "常用典型丝网规格参数",
    es: "Especificaciones seleccionadas de malla estándar",
    de: "Ausgewählte Standard-Drahtgewebespezifikationen",
    fr: "Spécifications sélectionnées de toiles standard",
    pt: "Especificações selecionadas de telas padrão"
  },
  lead: {
    en: "Representative specification examples across square weave, fine filter, twill Dutch, and sintered structures conforming to ASTM E2016 and ISO 9044 standards.",
    zh: "涵盖方孔筛网、精密滤网、荷兰编织与多层烧结结构的典型规格示例，符合 ASTM E2016 与 ISO 9044 工业公差标准。",
    es: "Ejemplos representativos de especificaciones en tejido cuadrado, filtro fino, tejido Dutch y estructuras sinterizadas según normas ASTM E2016 e ISO 9044.",
    de: "Typische Spezifikationsbeispiele für Quadratmaschen, Feinfilter, Tressengewebe und Sinterstrukturen gemäß den Normen ASTM E2016 und ISO 9044.",
    fr: "Exemples représentatifs de spécifications pour mailles carrées, filtres fins, tissages hollandais et structures frittées selon les normes ASTM E2016 et ISO 9044.",
    pt: "Exemplos representativos de especificações em malha quadrada, filtro fino, trama holandesa e estruturas sinterizadas conforme normas ASTM E2016 e ISO 9044."
  },
  thMesh: {
    en: "Mesh Count / Item",
    zh: "目数 / 规格",
    es: "Malla / Especificación",
    de: "Maschenzahl / Artikel",
    fr: "Nombre de mailles / Article",
    pt: "Malha / Item"
  },
  thType: {
    en: "Weave Structure",
    zh: "结构类型",
    es: "Estructura de tejido",
    de: "Gewebeart",
    fr: "Structure de tissage",
    pt: "Estrutura de trama"
  },
  thWire: {
    en: "Wire Diameter",
    zh: "丝径",
    es: "Diámetro de alambre",
    de: "Drahtdurchmesser",
    fr: "Diamètre de fil",
    pt: "Diâmetro do arame"
  },
  thAperture: {
    en: "Aperture / Rating",
    zh: "孔径 / 精度",
    es: "Abertura / Precisión",
    de: "Maschenweite / Feinheit",
    fr: "Ouverture / Seuil",
    pt: "Abertura / Precisão"
  },
  thOpenArea: {
    en: "Open Area",
    zh: "开孔率",
    es: "Área abierta",
    de: "Offene Siebfläche",
    fr: "Surface ouverte",
    pt: "Área aberta"
  },
  matrixFullChart: {
    en: "View Full Mesh Count & Aperture Chart",
    zh: "查阅完整目数孔径对照表",
    es: "Ver tabla completa de conteo de malla y abertura",
    de: "Vollständige Maschen- und Maschenweitentabelle anzeigen",
    fr: "Consulter le tableau complet du nombre de mailles et d'ouvertures",
    pt: "Ver tabela completa de contagem de malha e abertura"
  },
  customServiceLink: {
    en: "Custom OEM Slitting, Sintering & Fabricated Mesh",
    zh: "定制分条、切片与多层烧结深加工服务",
    es: "Servicios OEM de corte, sinterizado y piezas de malla fabricadas",
    de: "Kundenspezifischer Längsschnitt, Sintern & Konfektionierung",
    fr: "Découpe OEM, frittage et toiles confectionnées sur mesure",
    pt: "Serviços OEM de corte, sinterização e componentes de tela"
  }
};
export default function MeshSpecExamples() {
  const {
    lang
  } = useLanguage();
  const columns: DataTableColumn<MatrixItem>[] = useMemo(() => [{
    key: "mesh",
    header: pick(lang, copyData.thMesh),
    sticky: true,
    mobileTitle: true,
    cell: row => <span className="font-mono font-semibold text-foreground text-sm">
            {row.mesh}
          </span>
  }, {
    key: "type",
    header: pick(lang, copyData.thType),
    cell: row => <span className="inline-flex items-center px-2 py-0.5 rounded mf-caption bg-muted text-foreground border border-border">
            {pick(lang, row.type)}
          </span>
  }, {
    key: "wire",
    header: pick(lang, copyData.thWire),
    cell: row => <span className="font-mono text-sm text-foreground">{row.wire}</span>
  }, {
    key: "aperture",
    header: pick(lang, copyData.thAperture),
    cell: row => <span className="font-mono text-sm text-foreground font-semibold">
            {row.aperture}
          </span>
  }, {
    key: "openArea",
    header: pick(lang, copyData.thOpenArea),
    align: "right",
    cell: row => <span className="font-mono text-sm font-semibold text-foreground">
            {row.openArea}
          </span>
  }], [lang]);
  return;
}